/** @format */

import { isRef, Ref } from 'vue';
import { deepClone } from '@power/utils';

/**
 *
 * @param {boolean} deep 是否递归defer
 * @param {string} children children字段code
 */
interface DeferOptions {
	deep?: boolean;
	children?: string;
}

interface SetCurArrOptions extends DeferOptions {
	isAllSet?: boolean;
}

/**
 *
 * @param {number} deferLength 每次defer长度
 * @returns {any} cancel 取消函数,在onUnmounted中执行
 * @returns {any} setDeferArray 赋值函数
 */
export const useDeferSetArray = (
	deferLength = 10,
): {
	cancel: Function;
	setDeferArray: (curArr: any[] | Ref<any[]>, setArr: any[], options?: DeferOptions) => void;
} => {
	// deferId
	let deferId: number;

	const cancel = () => {
		cancelAnimationFrame(deferId);
	};

	const setCurArr = (curArr: any[] | Ref<any[]>, setArr: any[], isAllSet?: boolean) => {
		// 源数据是否为ref
		const isCurArrRef = isRef(curArr);

		if (isCurArrRef) {
			curArr.value = isAllSet ? setArr : curArr.value.concat(setArr);
		} else {
			setArr.forEach((item) => {
				curArr.push(item);
			});
		}
	};

	const setDeferArr = async (curArr: any[] | Ref<any[]>, deferArr: any[], options: SetCurArrOptions = {}) => {
		const { isAllSet, deep, children = 'children' } = options;

		// 源数据是否为ref
		const isCurArrRef = isRef(curArr);

		// 深层处理
		if (deep) {
			/**
			 *  1. 获取deferArr中children字段长度大于deferLength的数据
			 *  2. 先将该些数据children置空另存
			 *  3. 再进行数组赋值
			 *  4. 再对children过长的数据进行children赋值
			 */

			// 获取deferArr中children字段长度大于deferLength的数据,将数据置空另存
			const curArrLength = isCurArrRef ? (deepClone(curArr.value) as any).length : curArr.length;

			const deferChildrenArr = deferArr.reduce(
				(arr, itm, idx) => {
					if (itm[children]?.length > deferLength) {
						arr[0].push({ ...itm, [children]: [] });
						arr[1][`children_${curArrLength + idx}`] = itm[children];
					} else {
						arr[0].push(itm);
					}
					return arr;
				},
				[[], {}],
			);

			// 对数组进行赋值
			setCurArr(curArr, deferChildrenArr[0], isAllSet);

			// 对children过长的数据进行children赋值
			if (Object.keys(deferChildrenArr[1]).length) {
				for (const key in deferChildrenArr[1]) {
					const index = +key.split('_')[1];
					const setChildrenArr = deferChildrenArr[1][key];
					await setDeferArray(
						isCurArrRef ? curArr.value[index][children] : curArr[index][children],
						setChildrenArr,
						options,
					);
				}
			}
		} else {
			setCurArr(curArr, deferArr, isAllSet);
		}
	};

	/**
	 * @param  {any} curArr 需要赋值的数组
	 * @param  {any[]} setArr 赋值目标数组
	 * @param  {DeferOptions} options 配置项
	 */
	const setDeferArray = (curArr: any[] | Ref<any[]>, setArr: any[], options?: DeferOptions) =>
		new Promise((resolve, _reject) => {
			// 最大赋值次数
			const maxDeferCount = Math.ceil(setArr.length / deferLength);

			// 当前赋值位置
			let deferIndex = 0;

			const setDeferArrLoop = () => {
				if (deferIndex >= maxDeferCount) {
					resolve(true);
					return;
				}
				const deferArr = setArr.slice(deferIndex * deferLength, deferIndex * deferLength + deferLength);
				setDeferArr(curArr, deferArr, {
					...options,
					isAllSet: deferIndex === 0,
				});

				deferIndex++;
				deferId = requestAnimationFrame(setDeferArrLoop);
			};

			// 源数据是否为ref
			const isCurArrRef = isRef(curArr);
			if (isCurArrRef) {
				curArr.value.splice(0);
			} else {
				curArr.splice(0);
			}

			// 进入循环defer赋值
			deferId = requestAnimationFrame(setDeferArrLoop);
		});

	return {
		cancel,
		setDeferArray,
	};
};
